/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */

import { selector } from "recoil";

import { PROPERTY_TYPE_VALUES } from "@/api/mappings/property";

import { PropertyIds, PropertyHoldersState, PropertyHolder, SelectedPropertyId, SelectedPropertyIds } from "./atoms";

// 全物件の情報を取得
export const Properties = selector({
  key: "properties",
  get: ({ get }) => {
    const ids = get(PropertyIds);
    return ids.map((id) => {
      return get(PropertyHoldersState(id));
    });
  },
});

// 保有済み物件のみを取得（サンプル物件も含まない）
export const OwnedProperties = selector({
  key: "owned_properties",
  get: ({ get }) => {
    // プロパティが undefined でないものだけをフィルタリング
    const properties = get(Properties).filter((property) => property !== undefined);

    return properties.filter((property) => {
      return property.property.property_type === PROPERTY_TYPE_VALUES.owned;
    });
  },
});

// 保有済み物件のみを取得（サンプル物件も含む）
export const OwnedPropertiesWithSample = selector({
  key: "owned_properties_with_sample",
  get: ({ get }) => {
    const properties = get(Properties).filter((property) => property !== undefined);

    return properties.filter((property) => {
      return (
        property.property.property_type === PROPERTY_TYPE_VALUES.owned ||
        property.property.property_type === PROPERTY_TYPE_VALUES.owned_sample
      );
    });
  },
});

// 検討中物件のみを取得
export const ConsiderationProperties = selector({
  key: "consideration_properties",
  get: ({ get }) => {
    const properties = get(Properties).filter((property) => property !== undefined);

    return properties.filter((property) => {
      return property.property.property_type === PROPERTY_TYPE_VALUES.consideration;
    });
  },
});

// 提案物件のみを取得
export const ProposalProperties = selector({
  key: "proposal_properties",
  get: ({ get }) => {
    const properties = get(Properties);

    return properties.filter((property) => {
      return property.property.property_type === PROPERTY_TYPE_VALUES.proposal;
    });
  },
});

// 現在選択されている単一の物件を取得
export const SelectedProperty = selector({
  key: "selected_property",
  get: ({ get }) => {
    const selected_property_id = get(SelectedPropertyId);
    const properties = get(Properties);

    return properties.find((property) => {
      return property.id === selected_property_id;
    });
  },
});

// 現在選択されている複数の物件を取得（読み書き可能）
export const SelectedProperties = selector({
  key: "selected_properties",
  get: ({ get }): Array<PropertyHolder> => {
    const selected_property_ids = get(SelectedPropertyIds);
    const properties = get(Properties);
    return selected_property_ids.map((e) => {
      return properties.find((property) => {
        return property.id === e;
      });
    });
  },
  set: ({ set }, new_properties: Array<PropertyHolder>) => {
    new_properties.forEach((property) => {
      set(PropertyHoldersState(property.id), property);
    });
  },
});

// 保有物件があるかどうかの真偽値を取得
export const HasOwnedProperties = selector({
  key: "has_owned_properties",
  get: ({ get }) => {
    const ownedProperties = get(OwnedProperties);
    return ownedProperties.length > 0;
  },
});
